import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { SimpleDropdown } from '@/components/ui/simple-dropdown';
import { DATABASE_FIELDS, DATABASE_COMPARISON_OPERATORS, DATABASE_COMPARISON_EXPECTED } from '../condition-utils';
import type { AnswerOptionCondition } from 'shared/types/due-diligence';

interface DatabaseComparisonEditorProps {
  condition: AnswerOptionCondition;
  onUpdate: (updates: Partial<AnswerOptionCondition>) => void;
  extractionQuestions: Array<{
    fieldName: string;
    displayName: string;
    extractionLabel?: string | null;
    extractionValueType?: 'number' | 'string' | 'date' | 'boolean' | null;
  }>;
}

export function DatabaseComparisonEditor({ condition, onUpdate, extractionQuestions }: DatabaseComparisonEditorProps) {
  const sourceOptions = [
    { value: 'aiExtraction', label: 'AI extractie' },
    ...(extractionQuestions.length > 0 ? [{ value: 'extractionQuestion', label: 'Uit extractie vraag' }] : []),
  ];

  const extractionQuestionOptions = extractionQuestions.map((q) => ({
    value: q.fieldName,
    label: q.displayName || q.fieldName,
  }));

  // Flatten database fields for the dropdown
  const databaseFieldOptions = DATABASE_FIELDS.flatMap((group) =>
    group.fields.map((f) => ({
      value: f.key,
      label: `${group.category} › ${f.label}`,
    })),
  );

  const currentSource = condition.documentValueSource || 'aiExtraction';

  const getExampleText = () => {
    if (!condition.databaseField || !condition.dbComparisonOperator || !condition.dbComparisonExpected) {
      return 'Selecteer alle velden om een voorbeeld te zien.';
    }
    const fieldLabel =
      DATABASE_FIELDS.flatMap((g) => g.fields).find((f) => f.key === condition.databaseField)?.label ||
      condition.databaseField;
    const operatorLabel = DATABASE_COMPARISON_OPERATORS.find((o) => o.value === condition.dbComparisonOperator)?.label || '';
    const expectedLabel = DATABASE_COMPARISON_EXPECTED.find((o) => o.value === condition.dbComparisonExpected)?.label || '';
    return `Voorbeeld: Als documentwaarde ${operatorLabel} ${fieldLabel}, dan: "${expectedLabel}".`;
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-blue-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
        <p className="font-medium mb-1">Database vergelijking</p>
        <p>
          Vergelijkt een waarde uit het document met een waarde uit de database (gebouw of unit veld).
        </p>
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium">Document waarde bron</Label>
        <SimpleDropdown
          value={currentSource}
          onChange={(value) => onUpdate({ documentValueSource: value as AnswerOptionCondition['documentValueSource'] })}
          options={sourceOptions}
          placeholder="Selecteer bron..."
        />
      </div>

      {currentSource === 'aiExtraction' && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">AI extractie prompt</Label>
          <Input
            placeholder="bijv. Wat is het gebouwnummer in dit document?"
            value={condition.documentExtractionPrompt || ''}
            onChange={(e) => onUpdate({ documentExtractionPrompt: e.target.value })}
            className="text-sm"
          />
          <p className="text-xs text-muted-foreground">Instructie voor de AI om de waarde uit het document te halen.</p>
        </div>
      )}

      {currentSource === 'extractionQuestion' && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">Extractie vraag</Label>
          <SimpleDropdown
            value={condition.extractionQuestionFieldName || ''}
            onChange={(value) => onUpdate({ extractionQuestionFieldName: value })}
            options={extractionQuestionOptions}
            placeholder="Selecteer extractie vraag..."
          />
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-sm font-medium">Database veld</Label>
        <SimpleDropdown
          value={condition.databaseField || ''}
          onChange={(value) => onUpdate({ databaseField: value })}
          options={databaseFieldOptions}
          placeholder="Selecteer database veld..."
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm font-medium">Operator</Label>
          <SimpleDropdown
            value={condition.dbComparisonOperator || '=='}
            onChange={(value) => onUpdate({ dbComparisonOperator: value as AnswerOptionCondition['dbComparisonOperator'] })}
            options={DATABASE_COMPARISON_OPERATORS}
            placeholder="Selecteer operator..."
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-medium">Voorwaarde voldaan als</Label>
          <SimpleDropdown
            value={condition.dbComparisonExpected || 'match'}
            onChange={(value) => onUpdate({ dbComparisonExpected: value as AnswerOptionCondition['dbComparisonExpected'] })}
            options={DATABASE_COMPARISON_EXPECTED}
            placeholder="Selecteer uitkomst..."
          />
        </div>
      </div>

      <div className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg border border-border">
        <p className="font-medium mb-1">Uitleg:</p>
        <p>{getExampleText()}</p>
      </div>
    </div>
  );
}
